import React, { useMemo, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { loggedGet } from './loggedFetch';
import logger from '../../lib/logger';
import { TrajectoryChart, chartPalette, chartOtherColor } from '@techstack/ui';
import {
  formatFiscalYear,
  formatFiscalQuarter,
  fiscalYearRange,
  fiscalQuarterRange,
  fiscalAxisLabel,
  getFiscalCalendar,
} from '@techstack/core';
import { useFiscalBasisContext } from '../../contexts/FiscalBasisContext';
import FiscalBasisToggle from '../FiscalBasisToggle/FiscalBasisToggle';

// Agency colours come from the shared ramp — "consistent with shared
// component" was a comment on a hand-kept copy, and is now a fact.
const PALETTE = chartPalette;
const OTHER_COLOR = chartOtherColor; // a residual, never a series
const TOP_AGENCIES = 12;

function formatCurrencyShort(num) {
  const n = Number(num) || 0;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n)}`;
}

// FY labels and quarter spans are DERIVED from the active fiscal calendar —
// "FY27" means Jul 2026–Jun 2027 on the state basis and Aug 2026–Jul 2027 on
// the PANW basis, and the subtitle always says which. See @techstack/core.

function TrajectoryCharts({ vendor, includeLowConfidence, strict, productFamily, departmentIds, listName, years, onDrilldown }) {
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [granularity, setGranularity] = useState('year');
  // The VIEWER's fiscal calendar — never the viewed vendor's.
  const { basis, isDefaultBasis } = useFiscalBasisContext();

  // Build query string
  const qs = new URLSearchParams();
  if (includeLowConfidence) qs.set('includeLowConfidence', 'true');
  if (!strict) qs.set('strict', 'false');
  if (granularity === 'quarter') qs.set('granularity', 'quarter');
  if (productFamily) qs.set('product_family', productFamily);
  if (departmentIds?.length) qs.set('department_ids', departmentIds.join(','));
  if (years != null) qs.set('years', String(years));
  // Omit on the default basis so the request stays byte-identical to what it
  // was before the toggle existed.
  if (!isDefaultBasis) qs.set('basis', basis);
  const suffix = qs.toString() ? `?${qs}` : '';
  const path = `/api/vendor-analysis/${encodeURIComponent(vendor || '')}/trajectory${suffix}`;

  const { data, isLoading, error } = useQuery({
    // `basis` is in the key: flipping it must refetch, not re-label last
    // calendar's buckets.
    queryKey: ['vendor-analysis', 'trajectory', vendor, includeLowConfidence, strict, granularity, productFamily, departmentIds, years, basis],
    queryFn: loggedGet('TrajectoryCharts:fetch', path),
    enabled: !!vendor,
  });

  // Reset agency filter when vendor changes
  React.useEffect(() => { setSelectedAgency(null); }, [vendor]);

  // All-agency rollup for ranking and department IDs
  const allAgencies = useMemo(() => {
    if (!data) return [];
    const totals = {};
    const deptIds = {};
    for (const r of data.by_year_dept || []) {
      totals[r.agency] = (totals[r.agency] || 0) + (r.spend || 0);
      if (r.department_id != null) deptIds[r.agency] = r.department_id;
    }
    return Object.entries(totals)
      .map(([agency, spend]) => ({ agency, spend, department_id: deptIds[agency] }))
      .sort((a, b) => b.spend - a.spend);
  }, [data]);

  // Determine if quarterly data
  const isQuarterly = data?.granularity === 'quarter';

  // Trust the RESPONSE, not the request: label with the calendar the backend
  // actually bucketed on. `fiscal_basis` is the modern field; `year_basis`
  // is the legacy 'sfy' literal, kept working for older responses.
  const responseCal = useMemo(() => {
    const fb = data?.fiscal_basis;
    if (fb) {
      return {
        key: fb.key,
        label: fb.label,
        shortLabel: fb.short_label,
        fyStartMonth: fb.fy_start_month,
        fyStartDay: fb.fy_start_day,
        labelConvention: fb.label_convention,
      };
    }
    return data?.year_basis === 'sfy' ? getFiscalCalendar('ca_state') : null;
  }, [data]);

  // Is the X axis a fiscal year at all (vs. a plain calendar year)?
  const isSfy = Boolean(responseCal);
  const cal = responseCal || getFiscalCalendar('ca_state');

  const formatFy = useCallback((year) => formatFiscalYear(year, cal), [cal]);
  const formatFyQuarter = useCallback(
    (year, quarter) => formatFiscalQuarter(year, quarter, cal), [cal],
  );

  // Transform data for the shared TrajectoryChart component
  const { trajectoryData, topAgencyList, colorMap, agencyDeptIds, lineSeries } = useMemo(() => {
    const empty = { trajectoryData: [], topAgencyList: [], colorMap: {}, agencyDeptIds: {}, lineSeries: [] };
    if (!data) return empty;

    // Filter out rows with missing required fields
    const baseData = (data.by_year_dept || []).filter(
      r => r.year != null && (!isQuarterly || r.quarter != null)
    );
    const filtered = selectedAgency
      ? baseData.filter(r => r.agency === selectedAgency)
      : baseData;

    // Compute totals and rank agencies
    const totals = {};
    const deptIds = {};
    for (const r of filtered) {
      totals[r.agency] = (totals[r.agency] || 0) + (r.spend || 0);
      if (r.department_id != null) deptIds[r.agency] = r.department_id;
    }
    const ranked = Object.entries(totals).sort((a, b) => b[1] - a[1]);
    const TOP = selectedAgency ? 1 : TOP_AGENCIES;
    const topAgencyList = ranked.slice(0, TOP).map(([a]) => a);

    // Build color map
    const colorMap = {};
    topAgencyList.forEach((agency, idx) => {
      colorMap[agency] = PALETTE[idx % PALETTE.length];
    });
    colorMap['Other'] = OTHER_COLOR;

    // Pivot data for TrajectoryChart: { period, year, quarter?, totalSpend, agencies: { agency: spend } }
    const periodMap = new Map();
    for (const r of filtered) {
      const periodKey = isQuarterly ? `${r.year}-Q${r.quarter}` : String(r.year);
      const periodLabel = isQuarterly
        ? formatFyQuarter(r.year, r.quarter)
        : (isSfy ? formatFy(r.year) : String(r.year));

      if (!periodMap.has(periodKey)) {
        periodMap.set(periodKey, {
          period: periodLabel,
          year: r.year,
          quarter: r.quarter || null,
          totalSpend: 0,
          agencies: {},
          agencyCount: 0,
        });
      }
      const bucket = periodMap.get(periodKey);
      const agencyKey = topAgencyList.includes(r.agency) ? r.agency : 'Other';
      bucket.agencies[agencyKey] = (bucket.agencies[agencyKey] || 0) + (r.spend || 0);
      bucket.totalSpend += r.spend || 0;
    }

    // Sort by year/quarter
    const trajectoryData = Array.from(periodMap.values()).sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return (a.quarter || 0) - (b.quarter || 0);
    });

    // Build agency count from API data or compute
    const rawLine = (data.agency_count_by_year || []).slice();
    if (selectedAgency) {
      // For single agency, use spend as the line value
      trajectoryData.forEach(d => {
        d.agencyCount = d.totalSpend;
      });
    } else {
      // Map agency counts to periods
      for (const r of rawLine) {
        if (r.year == null || (isQuarterly && r.quarter == null)) continue;
        const periodLabel = isQuarterly
          ? formatFyQuarter(r.year, r.quarter)
          : (isSfy ? formatFy(r.year) : String(r.year));
        const match = trajectoryData.find(d => d.period === periodLabel);
        if (match) {
          match.agencyCount = Number(r.agency_count) || 0;
        }
      }
    }

    // Build line series for the separate LineChart
    let lineSeries;
    if (selectedAgency) {
      lineSeries = trajectoryData.map(s => ({
        period: s.period,
        year: s.year,
        quarter: s.quarter,
        total_spend: s.totalSpend,
        agency_count: 1,
      }));
    } else {
      lineSeries = rawLine
        .filter(r => r.year != null && (!isQuarterly || r.quarter != null))
        .map(r => ({
          period: isQuarterly ? formatFyQuarter(r.year, r.quarter) : (isSfy ? formatFy(r.year) : String(r.year)),
          year: r.year,
          quarter: r.quarter || null,
          agency_count: Number(r.agency_count) || 0,
          total_spend: Number(r.total_spend) || 0,
        }))
        .sort((a, b) => {
          if (a.year !== b.year) return a.year - b.year;
          return (a.quarter || 0) - (b.quarter || 0);
        });
    }

    return { trajectoryData, topAgencyList, colorMap, agencyDeptIds: deptIds, lineSeries };
  }, [data, selectedAgency, isQuarterly, isSfy, formatFy, formatFyQuarter]);

  // YoY summary. The current fiscal period is usually mid-flight (FY27 has
  // ~2 months of data in Aug 2026), so comparing it against the last complete
  // period reads as a catastrophic drop ("▼97%"). Exclude any period whose
  // end date hasn't passed and headline the two most recent COMPLETE periods,
  // surfacing the in-progress label separately.
  const yoy = useMemo(() => {
    if (!data || lineSeries.length < 2) return null;
    const sorted = lineSeries.filter(r => (r.total_spend || 0) > 0).sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return (a.quarter || 0) - (b.quarter || 0);
    });
    if (sorted.length < 2) return null;

    // MUST use the ACTIVE calendar. On the PANW basis a FY that ended Jul 31
    // is complete; measuring it against the state's Jun 30 would mark it
    // in-progress and silently drop a finished year out of the YoY headline.
    const periodEnd = (r) => {
      if (isSfy) {
        return r.quarter
          ? fiscalQuarterRange(r.year, r.quarter, cal).end
          : fiscalYearRange(r.year, cal).end;
      }
      if (r.quarter) {
        const endMonth = r.quarter * 3;
        const endDay = endMonth === 6 || endMonth === 9 ? 30 : 31;
        return `${r.year}-${String(endMonth).padStart(2, '0')}-${endDay}`;
      }
      return `${r.year}-12-31`;
    };
    const todayIso = new Date().toISOString().slice(0, 10);

    const complete = sorted.filter(r => periodEnd(r) < todayIso);
    const pool = complete.length >= 2 ? complete : sorted;
    const tail = sorted[sorted.length - 1];
    const inProgressLabel =
      complete.length >= 2 && periodEnd(tail) >= todayIso ? tail.period : null;

    const last = pool[pool.length - 1];
    let prev;
    if (isQuarterly && last.quarter) {
      prev = pool.find(r => r.year === last.year - 1 && r.quarter === last.quarter);
      if (!prev) prev = pool[pool.length - 2];
    } else {
      prev = pool[pool.length - 2];
    }
    if (!prev) return null;

    const lastSpend = last.total_spend || 0;
    const prevSpend = prev.total_spend || 0;
    const deltaPct = prevSpend > 0 ? ((lastSpend - prevSpend) / prevSpend) * 100 : null;

    return {
      percent: deltaPct != null ? Math.abs(deltaPct) : 0,
      direction: deltaPct == null ? 'flat' : deltaPct > 2 ? 'up' : deltaPct < -2 ? 'down' : 'flat',
      currentLabel: last.period,
      previousLabel: prev.period,
      currentFormatted: formatCurrencyShort(lastSpend),
      previousFormatted: formatCurrencyShort(prevSpend),
      inProgressLabel,
    };
  }, [data, lineSeries, isQuarterly, isSfy, cal]);

  // Handlers
  const handleSelectAgency = useCallback((agency) => {
    if (agency === 'Other') return;
    const next = selectedAgency === agency ? null : agency;
    logger.vendorAnalysis.info('TrajectoryCharts:agency-filter', {
      vendor, from: selectedAgency, to: next,
    });
    setSelectedAgency(next);
  }, [vendor, selectedAgency]);

  const handleGranularityChange = useCallback((newGran) => {
    logger.vendorAnalysis.info('TrajectoryCharts:granularity-change', {
      vendor, from: granularity, to: newGran,
    });
    setGranularity(newGran);
  }, [vendor, granularity]);

  const handleSegmentClick = useCallback((info) => {
    if (info.agency === 'Other') return;
    logger.vendorAnalysis.info('TrajectoryCharts:segment-click', {
      vendor, agency: info.agency, period: info.period,
    });
    onDrilldown({
      type: 'vendor',
      value: vendor,
      startDate: info.startDate,
      endDate: info.endDate,
      department: info.agency,
      departmentId: info.departmentId,
    });
  }, [vendor, onDrilldown]);

  if (isLoading) return <div className="vca-section vca-section__loading">Loading trajectory…</div>;
  if (error) return <div className="vca-section vca-section__error">Trajectory failed: {error.message}</div>;
  if (!data || trajectoryData.length === 0) {
    return (
      <div className="vca-section">
        <div className="vca-section__header">
          <h2>Trajectory</h2>
          <FiscalBasisToggle surface="trajectory-empty" />
        </div>
        <p className="vca-section__sub">No spend rows match the current filters.</p>
      </div>
    );
  }

  // Always name the calendar: "FY27" means two different windows depending on
  // the basis, so the axis must say which one it is.
  const subtitle = `Spend by ${isSfy ? fiscalAxisLabel(cal) : 'calendar year'}${isQuarterly ? ' quarter' : ''}, ${selectedAgency ? `filtered to ${selectedAgency}.` : `segmented by top ${Math.min(topAgencyList.length, TOP_AGENCIES)} agencies.`} Bars and legend items are clickable.${yoy?.inProgressLabel ? ` ${yoy.inProgressLabel} is in progress and excluded from the YoY change.` : ''}`;

  return (
    <div className="vca-section">
      <div className="vca-section__header">
        <FiscalBasisToggle surface="trajectory" />
      </div>
      <div className="vca-chart-grid">
        {/* Shared TrajectoryChart - stacked bar chart */}
        <TrajectoryChart
          data={trajectoryData}
          topAgencies={topAgencyList}
          colorMap={colorMap}
          agencyDeptIds={agencyDeptIds}
          height={320}
          showAgencyLine={false}
          yoyChange={yoy}
          onAgencyClick={handleSelectAgency}
          selectedAgency={selectedAgency}
          granularity={granularity}
          onGranularityChange={handleGranularityChange}
          showGranularityToggle={true}
          onSegmentClick={handleSegmentClick}
          isSfy={isSfy}
          fiscalCalendar={cal}
          title="Trajectory"
          subtitle={subtitle}
        />

        {/* Separate line chart (TekStak-specific: agency count or single-agency spend) */}
        <div style={{ width: '100%', height: 320 }}>
          <ResponsiveContainer>
            <LineChart data={lineSeries}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border, #2a2f38)" />
              <XAxis
                dataKey="period"
                /* interval 0 drew every FY label and they collided into
                   "FY15Y16Y17…" at panel widths; let recharts skip ticks. */
                interval="preserveStartEnd"
                minTickGap={16}
                angle={isQuarterly ? -45 : 0}
                textAnchor={isQuarterly ? 'end' : 'middle'}
                height={isQuarterly ? 60 : 30}
                tick={{ fill: 'var(--color-text-muted, #9aa3ad)', fontSize: 12 }}
              />
              <YAxis
                allowDecimals={false}
                tickFormatter={selectedAgency ? formatCurrencyShort : undefined}
                tick={{ fill: 'var(--color-text-muted, #9aa3ad)', fontSize: 12 }}
              />
              <Tooltip
                labelFormatter={(period) => period}
                formatter={(v, name) => name === 'Spend' ? formatCurrencyShort(v) : v}
                contentStyle={{
                  background: 'var(--color-surface-elevated, var(--color-surface, #1a1d24))',
                  border: '1px solid var(--color-border, #2a2f38)',
                  borderRadius: 8,
                  boxShadow: 'var(--shadow-lg, 0 4px 12px rgba(0,0,0,0.25))',
                }}
                labelStyle={{ color: 'var(--color-text, #f4f4f5)' }}
              />
              <Legend />
              {selectedAgency ? (
                <Line
                  type="monotone"
                  dataKey="total_spend"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot
                  name="Spend"
                  connectNulls
                />
              ) : (
                <Line
                  type="monotone"
                  dataKey="agency_count"
                  stroke="#4f46e5"
                  strokeWidth={2}
                  dot
                  name="Agencies with spend"
                  connectNulls
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Agency chips list */}
      <div className="vca-agency-list">
        <div className="vca-section__sub" style={{ marginBottom: 6 }}>
          Click an agency to filter the chart. Showing all {allAgencies.length}.
        </div>
        <div className="vca-agency-chips">
          {allAgencies.map(a => (
            <button
              key={a.agency}
              type="button"
              className={`vca-agency-chip ${selectedAgency === a.agency ? 'is-selected' : ''}`}
              onClick={() => handleSelectAgency(a.agency)}
              title={`Total ${formatCurrencyShort(a.spend)}`}
            >
              <span className="vca-agency-chip__name">{a.agency}</span>
              <span className="vca-agency-chip__spend">{formatCurrencyShort(a.spend)}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default TrajectoryCharts;
